/**
 * @file    layeredResolver.ts
 * @scope   SHARED
 * @summary Generic Chain of Responsibility: the first layer that returns a value wins.
 * @consumes Nothing.
 * @produces ResolverLayer, resolveFirst, resolvePerKey
 * @sideEffects None — pure.
 * @notes   One primitive reused for capabilities (Ph3), feature flags (Ph4) and Guardrails-vs-local
 *          (§13). Layers are ordered MOST specific first; `undefined` means "no opinion, ask next".
 */

export type ResolverLayer<TContext, TValue> = (context: TContext) => TValue | undefined;

/**
 * @param layers Ordered most specific first.
 * @param context Passed to every layer.
 * @param fallback Returned when no layer has an opinion.
 * @returns The first defined value.
 */
export const resolveFirst = <TContext, TValue>(
  layers: ResolverLayer<TContext, TValue>[],
  context: TContext,
  fallback: TValue
): TValue => {
  for (const layer of layers) {
    const value = layer(context);
    if (value !== undefined) return value;
  }
  return fallback;
};

/**
 * Resolves each key of `base` independently: per key, the most specific layer defining it wins.
 * WHY: a partition override of one flag must not wipe the rest of the project/base values.
 *
 * @param layers Partial objects, ordered most specific first.
 * @param context Passed to every layer.
 * @param base Complete object used for keys no layer defines.
 * @returns A new complete object.
 */
export const resolvePerKey = <TContext, TValue extends Record<string, unknown>>(
  layers: ResolverLayer<TContext, Partial<TValue>>[],
  context: TContext,
  base: TValue
): TValue => {
  const partials = layers.map((layer) => layer(context));
  const keys = Object.keys(base) as (keyof TValue)[];

  return keys.reduce((acc, key) => {
    acc[key] = resolveFirst(
      partials.map((partial) => () => partial?.[key]),
      undefined,
      base[key]
    ) as TValue[keyof TValue];
    return acc;
  }, {} as TValue);
};
